import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DOMAIN_STATUS_LABEL, HOST_HINT, hostSchema } from '@suite/contracts';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { EstadoCargando, EstadoError, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldDescription, FieldError, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@xiltrion/ui/input-group';
import { avisar } from '@xiltrion/ui/sonner';
import { Spinner } from '@xiltrion/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import { apiDelete, apiGet, apiPost, errorMessage } from '@/api/client';
import type { DnsCheckResult, DomainRow } from '@/api/types';

const avisarError = (e: unknown) => avisar.error(errorMessage(e));
const STATUS_VARIANT: Record<DomainRow['status'], 'secondary' | 'success-light' | 'success'> = { UNVERIFIED: 'secondary', VERIFIED: 'success-light', CERTIFIED: 'success' };
const fecha = (iso: string) => new Date(iso).toLocaleString('es-CO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
export const copiar = (texto: string, aviso: string) => void navigator.clipboard.writeText(texto).then(() => avisar.success(aviso), () => avisar.error('No se pudo copiar'));

/** Un valor de solo lectura con su botón de copiar (también en Entidad › Seguridad). */
export function Copiable({ valor, etiqueta }: { valor: string; etiqueta: string }) {
  return (
    <InputGroup>
      <InputGroupInput readOnly value={valor} aria-label={etiqueta} className="font-mono" onFocus={(e) => e.currentTarget.select()} />
      <InputGroupAddon align="inline-end"><InputGroupButton size="icon-xs" aria-label={`Copiar ${etiqueta}`} onClick={() => copiar(valor, `${etiqueta} copiado`)}><Icono nombre="mdi:content-copy" /></InputGroupButton></InputGroupAddon>
    </InputGroup>
  );
}

/**
 * Dominios propios de la entidad (solo superadministrador): `suite.ansv.gov.co` con un CNAME al host canónico de la
 * suite. La portada en ese host es la de la entidad y el edge emite el certificado cuando el dominio apunta aquí.
 */
export function EntityDomainsTab({ slug, entityName }: { slug: string; entityName: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['entity-domains', slug], queryFn: () => apiGet<{ canonicalHost: string; domains: DomainRow[] }>(`/entities/${slug}/domains`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['entity-domains', slug] });
  const [host, setHost] = useState('');
  const [hostError, setHostError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, DnsCheckResult>>({});
  const [removing, setRemoving] = useState<DomainRow | null>(null);
  if (q.error) return <EstadoError error={q.error} />;
  if (!q.data) return <EstadoCargando />;
  const { canonicalHost, domains } = q.data;
  const ejemplo = domains[0]?.host ?? `suite.${slug}.gov.co`;

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = hostSchema.safeParse(host);
    if (!parsed.success) { setHostError(parsed.error.issues[0]?.message ?? HOST_HINT); return; }
    setHostError(null);
    setAdding(true);
    void apiPost<DomainRow>(`/entities/${slug}/domains`, { host: parsed.data }).then(async () => { setHost(''); avisar.success('Dominio agregado', { description: 'Cuando el cliente ponga el CNAME, verifícalo.' }); await refresh(); }, avisarError).finally(() => setAdding(false));
  };
  const verify = (d: DomainRow) => {
    setVerifying(d.host);
    void apiPost<{ domain: DomainRow; check: DnsCheckResult }>(`/entities/${slug}/domains/${encodeURIComponent(d.host)}/verify`).then(async (r) => {
      setChecks((c) => ({ ...c, [d.host]: r.check }));
      if (r.check.ok) avisar.success(`${d.host} apunta a la suite`, { description: `Por ${r.check.via === 'CNAME' ? 'CNAME' : 'dirección'} (${r.check.target}).` });
      else avisar.warning(`${d.host} todavía no apunta a la suite`, { description: r.check.detail });
      await refresh();
    }, avisarError).finally(() => setVerifying(null));
  };

  const columnas: ColumnaTabla<DomainRow>[] = [
    { key: 'host', titulo: 'Dominio', valor: (d) => d.host, render: (d) => <span className="font-mono">{d.host}</span>, secundario: (d) => `Registrado el ${fecha(d.createdAt)}` },
    { key: 'status', titulo: 'Estado', valor: (d) => DOMAIN_STATUS_LABEL[d.status], unaLinea: true, render: (d) => {
      const check = checks[d.host];
      const badge = <Badge variant={STATUS_VARIANT[d.status]}>{DOMAIN_STATUS_LABEL[d.status]}</Badge>;
      const why = check && !check.ok ? check.detail : d.status === 'UNVERIFIED' ? 'Aún no se ha comprobado que el DNS apunte a la suite.' : d.status === 'VERIFIED' ? 'Apunta a la suite; el certificado se emite con la primera visita por HTTPS.' : `El edge pidió su certificado${d.tlsAskedAt ? ` (${fecha(d.tlsAskedAt)})` : ''}.`;
      return <Tooltip><TooltipTrigger asChild>{badge}</TooltipTrigger><TooltipContent className="max-w-xs">{why}</TooltipContent></Tooltip>;
    }, secundario: (d) => { const c = checks[d.host]; return d.verifiedAt ? `Verificado el ${fecha(d.verifiedAt)}` : c && !c.ok ? c.detail : null; } },
    { key: 'actions', titulo: '', valor: () => '', ordenable: false, render: (d) => (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="outline" disabled={verifying === d.host} onClick={(e) => { e.stopPropagation(); verify(d); }}>{verifying === d.host ? <Spinner /> : <Icono nombre="mdi:dns-outline" />}Verificar</Button>
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setRemoving(d); }}>Quitar</Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Dominios propios</CardTitle>
          <CardDescription>Con un dominio propio, la gente de {entityName} entra por <span className="font-mono">{ejemplo}</span> y ve la portada de su entidad, sin parámetros en la dirección. El cliente lo apunta a la suite con un CNAME; el certificado HTTPS lo emite el edge cuando el dominio apunta aquí.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={add}>
            <Field data-invalid={!!hostError || undefined}>
              <FieldLabel htmlFor="dominio-host">Dominio</FieldLabel>
              <div className="flex gap-2">
                <Input id="dominio-host" value={host} onChange={(e) => { setHost(e.target.value); setHostError(null); }} placeholder={`suite.${slug}.gov.co`} aria-invalid={!!hostError || undefined} autoComplete="off" spellCheck={false} />
                <Button type="submit" className="shrink-0" disabled={adding || !host.trim()}>{adding ? <Spinner /> : <Icono nombre="mdi:plus" />}Agregar</Button>
              </div>
              {hostError ? <FieldError>{hostError}</FieldError> : <FieldDescription>{HOST_HINT}</FieldDescription>}
            </Field>
          </form>
          <Tabla filas={domains} columnas={columnas} claveFila={(d) => d.host} densa vacio={<EstadoVacio titulo="Sin dominios propios" descripcion={`La gente de ${entityName} entra por el dominio de la suite con ?entidad=${slug}.`} />} />
        </CardContent>
      </Card>
      <Alert variant="info">
        <Icono nombre="mdi:dns-outline" />
        <AlertTitle>Lo que configura el cliente en su DNS</AlertTitle>
        <AlertDescription className="w-full space-y-3">
          <p>Un registro CNAME del dominio propio al host canónico de la suite. Cuando el DNS ya responda (puede tardar hasta unas horas), «Verificar» lo comprueba desde el servidor y la portada en ese dominio queda activa; el certificado HTTPS se emite solo en la primera visita.</p>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <Copiable valor={ejemplo} etiqueta="Nombre del registro" />
            <Badge variant="outline" className="justify-self-center">CNAME</Badge>
            <Copiable valor={canonicalHost} etiqueta="Destino del registro" />
          </div>
          <Button size="sm" variant="outline" onClick={() => copiar(`${ejemplo}  IN  CNAME  ${canonicalHost}.`, 'Registro copiado')}><Icono nombre="mdi:content-copy" />Copiar el registro completo</Button>
          <p className="text-xs text-muted-foreground">Si el proveedor de DNS no admite CNAME en ese nombre (p. ej. el dominio raíz), vale un registro A con la dirección de {canonicalHost}.</p>
        </AlertDescription>
      </Alert>
      <ConfirmarDialogo
        abierto={!!removing}
        onAbiertoChange={(v) => { if (!v) setRemoving(null); }}
        titulo={`Quitar ${removing?.host ?? ''}`}
        descripcion="La portada en ese dominio deja de ser la de la entidad y el edge no renueva su certificado. El registro DNS del cliente no cambia."
        textoConfirmar="Quitar"
        destructivo
        icono="mdi:web-off"
        onConfirmar={() => apiDelete(`/entities/${slug}/domains/${encodeURIComponent(removing!.host)}`).then(async () => { avisar.success('Dominio quitado'); setRemoving(null); await refresh(); }, (e: unknown) => { avisarError(e); })}
      />
    </div>
  );
}
